export default class CustomSelect {
    static init(): void {
        this.customSelectInit();
    }

    static customSelectInit(): void {
        // TypeScript interfaces
        interface SelectChangeDetail {
            value: string;
            text: string;
            option: HTMLElement;
        }

        interface CustomSelectManagerWindow extends Window {
            customSelectManager?: CustomSelectManager;
        }

        // Použijeme type assertion místo declare
        const windowWithManager = window as CustomSelectManagerWindow;

        class CustomSelectInstance {
            private container: HTMLElement;
            private header: HTMLElement;
            private arrow: HTMLElement;
            private options: HTMLElement;
            private selectedValue: HTMLElement;
            private optionElements: NodeListOf<HTMLElement>;
            private isOpen: boolean = false;
            private selectedOption: HTMLElement | null = null;
            public selectType: 'pricing' | 'simple';

            constructor(container: HTMLElement) {
                if (!container) {
                    throw new Error('CustomSelect: Kontejner není definován');
                }

                this.container = container;

                // Určení typu selectu podle CSS třídy
                this.selectType = container.classList.contains('select-container-simple') ? 'simple' : 'pricing';

                // Bezpečné načtení elementů
                this.header = container.querySelector('.select-header') as HTMLElement;
                this.arrow = container.querySelector('.select-arrow') as HTMLElement;
                this.options = container.querySelector('.select-options') as HTMLElement;
                this.selectedValue = container.querySelector('.selected-value') as HTMLElement;
                this.optionElements = container.querySelectorAll('.option') as NodeListOf<HTMLElement>;

                // Kontrola existence klíčových elementů
                if (!this.header || !this.arrow || !this.options || !this.selectedValue) {
                    throw new Error('CustomSelect: Chybí potřebné elementy v kontejneru');
                }

                if (this.optionElements.length === 0) {
                    throw new Error('CustomSelect: Nebyly nalezeny žádné možnosti (.option)');
                }

                this.init();
            }

            private init(): void {
                // Klik na header pro otevření/zavření
                this.header.addEventListener('click', (e: Event) => {
                    e.stopPropagation();
                    this.toggle();
                });

                // Klik na jednotlivé možnosti
                this.optionElements.forEach((option: HTMLElement) => {
                    option.addEventListener('click', (e: Event) => {
                        e.stopPropagation();
                        this.selectOption(option);
                    });
                });

                // Zavření při kliku mimo
                document.addEventListener('click', () => {
                    this.close();
                });

                // Klávesnice
                this.container.addEventListener('keydown', (e: KeyboardEvent) => {
                    this.handleKeyboard(e);
                });

                // Nastavení tabindex pro přístupnost
                this.header.setAttribute('tabindex', '0');
            }

            private toggle(): void {
                if (this.isOpen) {
                    this.close();
                } else {
                    this.open();
                }
            }

            private open(): void {
                // Zavře ostatní selecty před otevřením tohoto
                this.closeOthers();

                this.isOpen = true;
                this.header.classList.add('active');
                this.arrow.classList.add('rotated');
                this.options.classList.add('open');
            }

            public close(): void {
                this.isOpen = false;
                this.header.classList.remove('active');
                this.arrow.classList.remove('rotated');
                this.options.classList.remove('open');
            }

            // Metoda pro programové zavření všech ostatních selectů
            private closeOthers(): void {
                if (windowWithManager.customSelectManager) {
                    windowWithManager.customSelectManager.selects.forEach((select: CustomSelectInstance) => {
                        if (select !== this && select.isOpen) {
                            select.close();
                        }
                    });
                }
            }

            private selectOption(option: HTMLElement): void {
                const value: string = option.dataset.value || '';
                const text: string = option.dataset.text || '';

                // Odstranění předchozího výběru
                if (this.selectedOption) {
                    this.selectedOption.classList.remove('selected');
                }

                // Nastavení nového výběru
                this.selectedOption = option;
                option.classList.add('selected');

                // Zpracování podle typu selectu
                if (this.selectType === 'simple') {
                    this.handleSimpleSelect(option, value);
                } else {
                    this.handlePricingSelect(option, value);
                }

                this.close();

                // Vytvoření custom eventu
                const changeEvent = new CustomEvent<SelectChangeDetail>('selectChange', {
                    detail: { value, text, option },
                    bubbles: true, // Toto umožní eventu propagovat nahoru do document
                });
                this.container.dispatchEvent(changeEvent);

                // Find the associated hidden input for this custom select
                let valueInput: HTMLInputElement | null = null;
                
                // First try to find a hidden input within the same container or nearby
                valueInput = this.container.querySelector('input[data-select-value]') as HTMLInputElement;
                
                // If not found within container, look for it in the parent elements
                if (!valueInput) {
                    let parent = this.container.parentElement;
                    while (parent && !valueInput) {
                        valueInput = parent.querySelector('input[data-select-value]') as HTMLInputElement;
                        parent = parent.parentElement;
                    }
                }
                
                // Fallback to specific ID-based lookups for known selects
                if (!valueInput) {
                    if (this.container.id === 'phone-precode-select') {
                        valueInput = document.getElementById('phone-precode-input') as HTMLInputElement;
                    } else if (this.container.id === 'country-select') {
                        valueInput = document.getElementById('country-input') as HTMLInputElement;
                    } else if (this.container.id === 'delivery-phone-precode-select') {
                        valueInput = document.getElementById('delivery-phone-precode-input') as HTMLInputElement;
                    }
                }

                if (valueInput) {
                    valueInput.value = value;
                    console.log(`CustomSelect: Updated hidden input with value: ${value}`);
                } else {
                    console.warn(`CustomSelect: Could not find associated hidden input for container:`, this.container);
                }
            }

            private handleSimpleSelect(option: HTMLElement, _value: string): void {
                // Pro jednoduchý select jen zkopírujeme text
                const optionText: string = option.textContent || '';
                this.selectedValue.textContent = optionText;
                this.selectedValue.style.color = '#333';
            }

            private handlePricingSelect(option: HTMLElement, value: string): void {
                // Pro cenový select - původní logika
                if (value === 'other') {
                    this.selectedValue.textContent = 'Jiné množství';
                    this.selectedValue.style.color = '#dc3545';
                } else {
                    // Zkrácený text pro header
                    const quantityElement = option.querySelector('.option-quantity') as HTMLElement;
                    const totalElement = option.querySelector('.option-total') as HTMLElement;

                    if (quantityElement && totalElement) {
                        const quantity: string = quantityElement.textContent || '';
                        const total: string = totalElement.textContent || '';
                        this.selectedValue.textContent = `${quantity} - ${total}`;
                        this.selectedValue.style.color = '#333';
                    }
                }
            }

            private handleKeyboard(e: KeyboardEvent): void {
                if (!this.header.matches(':focus')) return;

                switch (e.key) {
                    case 'Enter':
                    case ' ':
                        e.preventDefault();
                        this.toggle();
                        break;
                    case 'Escape':
                        this.close();
                        break;
                    case 'ArrowDown':
                        e.preventDefault();
                        if (!this.isOpen) {
                            this.open();
                        }
                        break;
                    case 'ArrowUp':
                        e.preventDefault();
                        if (this.isOpen) {
                            this.close();
                        }
                        break;
                }
            }

            // Getter pro isOpen
            public get isSelectOpen(): boolean {
                return this.isOpen;
            }

            // Getter pro container (pro manager)
            public get containerElement(): HTMLElement {
                return this.container;
            }
        }

        // Univerzální inicializace pro více selectů
        class CustomSelectManager {
            public selects: CustomSelectInstance[] = [];

            constructor() {
                this.init();
            }

            private init(): void {
                // Najdi všechny select kontejnery na stránce (oba typy)
                const selectContainers = document.querySelectorAll<HTMLElement>(
                    '.select-container, .select-container-simple',
                );

                if (selectContainers.length === 0) {
                    //console.warn('CustomSelect: Nebyl nalezen žádný select kontejner');
                    return;
                }

                selectContainers.forEach((container: HTMLElement, index: number) => {
                    try {
                        // Kontrola, zda kontejner obsahuje potřebné elementy
                        if (!this.validateContainer(container)) {
                            console.warn(`CustomSelect: Kontejner #${index} nemá správnou strukturu`);
                            return;
                        }

                        const customSelect = new CustomSelectInstance(container);
                        this.selects.push(customSelect);

                        // Přidání jedinečného ID pokud neexistuje
                        if (!container.id) {
                            const type: string = container.classList.contains('select-container-simple')
                                ? 'simple'
                                : 'pricing';
                            container.id = `custom-select-${type}-${index}`;
                        }

                        // Listener pro změny
                        container.addEventListener('selectChange', (e: Event) => {
                            const customEvent = e as CustomEvent<SelectChangeDetail>;
                            console.log(`Select ${container.id} změněn:`, customEvent.detail);

                            // Logika podle typu selectu
                            if (customSelect.selectType === 'pricing' && customEvent.detail.value === 'other') {
                                alert(`Select ${container.id}: Přesměrování na stránku pro vlastní množství...`);
                            } else if (customSelect.selectType === 'simple') {
                                // Zde můžete přidat logiku pro jednoduchý select
                                console.log(`Simple select změněn na: ${customEvent.detail.value}`);
                            }
                        });
                    } catch (error) {
                        console.error(`CustomSelect: Chyba při inicializaci kontejneru #${index}:`, error);
                    }
                });

                console.log(`CustomSelect: Inicializováno ${this.selects.length} selectů`);
            }

            private validateContainer(container: HTMLElement): boolean {
                const requiredSelectors: string[] = [
                    '.select-header',
                    '.select-arrow',
                    '.select-options',
                    '.selected-value',
                    '.option',
                ];

                return requiredSelectors.every((selector: string) => {
                    const element = container.querySelector(selector);
                    if (!element) {
                        console.warn(`CustomSelect: Nenalezen element ${selector} v kontejneru`);
                        return false;
                    }
                    return true;
                });
            }

            // Metoda pro získání všech selectů
            public getAllSelects(): CustomSelectInstance[] {
                return this.selects;
            }

            // Metoda pro získání selectů podle typu
            public getSelectsByType(type: 'pricing' | 'simple'): CustomSelectInstance[] {
                return this.selects.filter((select: CustomSelectInstance) => select.selectType === type);
            }

            // Metoda pro získání selectu podle ID kontejneru
            public getSelectById(containerId: string): CustomSelectInstance | undefined {
                return this.selects.find((select: CustomSelectInstance) => select.containerElement.id === containerId);
            }

            // Metoda pro zavření všech selectů
            public closeAll(): void {
                this.selects.forEach((select: CustomSelectInstance) => select.close());
            }
        }

        // Inicializace
        try {
            windowWithManager.customSelectManager = new CustomSelectManager();
        } catch (error) {
            console.error('CustomSelect: Chyba při inicializaci:', error);
        }
    }
}
